import { initializeApp, getApps } from "firebase/app";
import { getMessaging, getToken as getMessagingToken, onMessage } from "firebase/messaging";
import { getFirestore } from "firebase/firestore";
import { getAuth, GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult } from "firebase/auth";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase app only once
let app;
try {
  if (getApps().length === 0) {
    app = initializeApp(firebaseConfig);
    console.log('Firebase app initialized in utility file');
  } else {
    app = getApps()[0];
    console.log('Using existing Firebase app in utility file');
  }
} catch (error) {
  console.error('Error initializing Firebase app:', error);
}

// Initialize Firestore
let db = null;
try {
  if (app) {
    db = getFirestore(app);
    console.log('Firestore initialized successfully');
  }
} catch (error) {
  console.error('Error initializing Firestore:', error);
}

// Initialize Firebase Auth
let auth = null;
let googleProvider = null;
try {
  if (app) {
    auth = getAuth(app);
    googleProvider = new GoogleAuthProvider();
    // Add scopes for user profile and email
    googleProvider.addScope('profile');
    googleProvider.addScope('email');
    console.log('Firebase Auth initialized successfully');
  }
} catch (error) {
  console.error('Error initializing Firebase Auth:', error);
}

// Initialize messaging with error handling
let messaging = null;
try {
  if (app && typeof window !== "undefined" && "Notification" in window) {
    messaging = getMessaging(app);
    console.log('Firebase messaging initialized successfully');
  } else if (typeof window === "undefined") {
    console.log('Server-side rendering detected, skipping messaging initialization');
  } else {
    console.warn('Notifications not supported in this browser');
  }
} catch (error) {
  console.error('Error initializing Firebase messaging:', error);
}

export const requestNotificationPermission = async () => {
  try {
    if (!messaging) {
      console.warn('Firebase messaging not available');
      return null;
    }
    
    const permission = await Notification.requestPermission();
    console.log('Notification permission result:', permission);
    
    if (permission === "granted") {
      const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
      if (!vapidKey) {
        console.error('VAPID key not found in environment variables');
        return null;
      }

      const token = await getMessagingToken(messaging, {
        vapidKey: vapidKey
      });
      
      if (token) {
        console.log('FCM Token obtained in utility:', token.substring(0, 20) + '...');
        return token;
      } else {
        console.warn('No FCM token received');
        return null;
      }
    } else {
      console.warn('Notification permission denied:', permission);
      return null;
    }
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return null;
  }
};

// Google Sign-In with Popup
export const signInWithGooglePopup = async () => {
  try {
    if (!auth || !googleProvider) {
      throw new Error('Firebase Auth not initialized');
    }
    const result = await signInWithPopup(auth, googleProvider);
    return result;
  } catch (error) {
    console.error('Error signing in with Google popup:', error);
    throw error;
  }
};

// Google Sign-In with Redirect (for mobile devices)
export const signInWithGoogleRedirect = async () => {
  try {
    if (!auth || !googleProvider) {
      throw new Error('Firebase Auth not initialized');
    }
    await signInWithRedirect(auth, googleProvider);
  } catch (error) {
    console.error('Error signing in with Google redirect:', error);
    throw error;
  }
};

// Get redirect result after redirect sign-in
export const getGoogleRedirectResult = async () => {
  try {
    if (!auth) {
      throw new Error('Firebase Auth not initialized');
    }
    const result = await getRedirectResult(auth);
    return result;
  } catch (error) {
    console.error('Error getting redirect result:', error);
    throw error;
  }
};

// Export everything needed
export { 
  messaging, 
  onMessage, 
  db, 
  getMessagingToken as getToken,
  app,
  auth,
  googleProvider
};
